import { fabric } from "fabric";
import { SheetLabel } from "@/types";

export const getSheetScale = (width: number, sheetWidth: number, dpi: number) => {
  const sheetWidthPx = sheetWidth * dpi;
  return width / sheetWidthPx;
};

export const checkBleed = (labelGroup: fabric.Group, bleed: number): fabric.Object | undefined => {
  if (bleed) {
    const actualLabelSize = labelGroup._objects.find((obj) => obj.name === "actualLabelSize");
    if (actualLabelSize) labelGroup.remove(actualLabelSize);

    return labelGroup._objects.find((obj) => obj.name === "labelCanvas");
  } else {
    return labelGroup._objects.find((obj) => obj.name === "actualLabelSize");
  }
};

export const setLabelGroupAngle = (labelGroup: fabric.Group, isRotated: boolean) => {
  if (isRotated) {
    labelGroup.set({
      angle: labelGroup.angle !== undefined ? labelGroup.angle - 90 : 0 - 90,
    });
  }
};

export const setClipPath = (
  labelGroup: fabric.Group,
  label: fabric.Object,
  shapeId: number,
  maskId: number,
  maskUrl?: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    switch (shapeId) {
      case 3:
      case 4: {
        labelGroup.clipPath = new fabric.Rect({
          left: label.left,
          top: label.top,
          width: label.width,
          height: label.height,
          originX: "center",
          originY: "center",
          rx: (label as fabric.Rect).rx,
          ry: (label as fabric.Rect).ry,
          angle: labelGroup.angle,
        });

        resolve();
        break;
      }
      case 5: {
        labelGroup.clipPath = new fabric.Ellipse({
          left: label.left,
          top: label.top,
          scaleX: label.scaleX,
          scaleY: label.scaleY,
          originX: "center",
          originY: "center",
          rx: (label.width as number) / 2,
          ry: (label.height as number) / 2,
          angle: labelGroup.angle,
        });

        resolve();
        break;
      }
      case 2:
      case 6:
      case 7:
      case 8: {
        try {
          if (maskUrl) {
            fabric.loadSVGFromURL(maskUrl, function (results, options) {
              const clipPath = fabric.util.groupSVGElements(results, options) as fabric.Object;

              clipPath.set({
                left: label.left,
                top: label.top,
                width: label.width,
                height: label.height,
                scaleX: label.scaleX,
                scaleY: label.scaleY,
                originX: "center",
                originY: "center",
                angle: labelGroup.angle,
              });

              labelGroup.clipPath = clipPath;

              resolve();
            })
          } else {
            fabric.loadSVGFromURL(require(`@/assets/labels/${maskId}.svg`), function(
              results,
              options
            ) {
              const clipPath = fabric.util.groupSVGElements(results, options) as fabric.Object;

              clipPath.set({
                left: label.left,
                top: label.top,
                width: label.width,
                height: label.height,
                scaleX: label.scaleX,
                scaleY: label.scaleY,
                originX: "center",
                originY: "center",
                angle: labelGroup.angle,
              });

              labelGroup.clipPath = clipPath;

              resolve();
            });
          }
        } catch (e) {
          reject(e);
        }
        break;
      }
      case 10: {
        labelGroup.clipPath = new fabric.Circle({
          left: label.left,
          top: label.top,
          radius: (label as fabric.Circle).radius,
          originX: "center",
          originY: "center",
          angle: labelGroup.angle,
        });

        resolve();
        break;
      }
    }
  });
};

export const addGroups = async (
  currentLabel: SheetLabel,
  labelGroup: fabric.Group,
  label: fabric.Object,
  sheetScale: number,
  canvas: fabric.Canvas,
  sheetWidth: number,
  sheetHeight: number
) => {
  const cl = currentLabel;

  let leftMargin = cl.left_margin;
  let topMargin = cl.top_margin;
  let horizSpacing = cl.horizontal_spacing;
  let verticSpacing = cl.vertical_spacing;

  if (cl.bleed) {
    if (cl.bleed_size) {
      leftMargin -= cl.bleed_size;
      topMargin -= cl.bleed_size;

      horizSpacing -= cl.bleed_size * 2;
      verticSpacing -= cl.bleed_size * 2;
    } else {
      leftMargin -= 0.125;
      topMargin -= 0.125;

      horizSpacing -= 0.125 * 2;
      verticSpacing -= 0.125 * 2;
    }
  }

  leftMargin = (leftMargin / cl.sheet_size_width) * sheetWidth;
  topMargin = (topMargin / cl.sheet_size_height) * sheetHeight;

  horizSpacing = (horizSpacing / cl.sheet_size_width) * sheetWidth;
  verticSpacing = (verticSpacing / cl.sheet_size_height) * sheetHeight;

  let left = leftMargin;
  let top = topMargin;

  async function* asyncIter() {
    let i = 0;
    while (i < cl.labels_per_sheet) {
      yield i++;
    }
  }

  for await (const i of asyncIter()) {
    labelGroup.clone(
      function(obj: fabric.Group) {
        if (i > 0 && i % cl.labels_per_row === 0) {
          left = leftMargin;
          top += (label.height! * label.scaleY!) * sheetScale + verticSpacing;
        }

        obj.set({
          left: (obj.left as number) + left,
          top: (obj.top as number) + top,
          selectable: false,
          evented: false,
        });

        left += (label.width! * label.scaleX!) * sheetScale + horizSpacing;

        canvas.add(obj);
      },
      ["name", "type"]
    );
  }

  canvas.renderAll();
};
